import { cartMixin } from './cart';
import { MAP_CONTAINER_ID, MODULE_ID } from './constants';
import { deliveryMixin } from './delivery';
import { suggestionsMixin } from './suggestions';
import { appTemplate } from './template';

const YaDeliveryMapApp = {
	mixins: [suggestionsMixin, deliveryMixin, cartMixin],

	props: {
		params: { type: Object, required: true },
	},

	data()
	{
		const defaultWarehouse = this.params.mapSettings?.points?.warehouse || '';

		return {
			api: null,
			price: 0,
			distance: 0,
			address: '',
			total: { price: 0, weight: 0 },
			orderPrice: '',
			orderWeight: '',
			isInitializing: true,
			isCalculating: false,
			hasCalculated: false,
			deliveryType: null,
			warehouseCode: null,
			errors: [],
			animatedPrice: 0,
			cartData: null,
			isLoadingCart: false,
			showCartPanel: false,
			mapExpanded: false,
			selectedWarehouse: defaultWarehouse,
			isChangingWarehouse: false,
			animationFrameId: null,
		};
	},

	computed: {
		isModal()
		{
			return this.params.displayMap === 'MODAL';
		},

		isPageMap()
		{
			return this.params.displayMap === 'PAGE';
		},

		isYandexSuggest()
		{
			return this.params.typePrompts === 'yandex';
		},

		showCostField()
		{
			return this.params.hideCostField !== 'Y';
		},

		showWeightField()
		{
			return this.params.hideWeightField !== 'Y';
		},

		showOrderParams()
		{
			return this.showCostField || this.showWeightField;
		},

		isForbidden()
		{
			return this.price < 0;
		},

		currency()
		{
			return this.params.currency || this.loc('YADELIVERY_MAP_CURRENCY');
		},

		costBlockState()
		{
			if (this.isInitializing)
			{
				return 'loading';
			}

			if (this.isCalculating)
			{
				return 'calculating';
			}

			if (!this.hasCalculated)
			{
				return 'initial';
			}

			if (this.isForbidden)
			{
				return 'forbidden';
			}

			if (this.hasErrors)
			{
				return 'error';
			}

			return 'success';
		},

		showZoneType()
		{
			return this.params.showZoneType && this.hasCalculated && this.deliveryType !== null;
		},

		deliveryTimeText()
		{
			if (!this.hasCalculated || this.isForbidden)
			{
				return '';
			}

			if (this.deliveryType === 'in')
			{
				return this.params.inZoneDeliveryTime || '';
			}

			return this.params.outZoneDeliveryTime || '';
		},

		warehouseName()
		{
			if (!this.params.showWarehouse || !this.warehouseCode || !this.params.warehouses)
			{
				return '';
			}

			return this.params.warehouses[this.warehouseCode] || '';
		},

		formattedDistance()
		{
			if (!this.params.showDistance || this.distance <= 0)
			{
				return null;
			}

			const meters = Math.round(this.distance);

			if (meters < 1000)
			{
				return {
					value: meters,
					unit: this.loc('YADELIVERY_MAP_M'),
				};
			}

			return {
				value: (meters / 1000).toFixed(1),
				unit: this.loc('YADELIVERY_MAP_KM'),
			};
		},

		weightUnit()
		{
			return this.params.weightUnit || 'g';
		},

		weightUnitLabel()
		{
			const key = `YADELIVERY_MAP_WEIGHT_UNIT_${this.weightUnit.toUpperCase()}`;
			return this.loc(key);
		},

		weightToGramsMultiplier()
		{
			const multipliers = { g: 1, kg: 1000, t: 1000000 };
			return multipliers[this.weightUnit] || 1;
		},

		weightInKg()
		{
			const value = Number(this.orderWeight);
			if (!value || value <= 0 || this.weightUnit === 'kg')
			{
				return '';
			}

			const grams = value * this.weightToGramsMultiplier;
			const kg = (grams / 1000).toFixed(2).replace(/\.?0+$/, '');
			return `${kg} ${this.loc('YADELIVERY_MAP_KG')}`;
		},

		hasErrors()
		{
			return this.errors.length > 0;
		},

		showCart()
		{
			return this.params.enableCart;
		},

		showWarehouseSelector()
		{
			return Array.isArray(this.params.availableWarehouses)
				&& this.params.availableWarehouses.length > 1;
		},

		mapStyle()
		{
			const height = this.mapExpanded ? this.params.mapHeight * 1.5 : this.params.mapHeight;
			return { height: height + 'px' };
		},

		forbiddenText()
		{
			return this.params.forbiddenMessage || this.loc('YADELIVERY_MAP_FORBIDDEN');
		},
	},

	mounted()
	{
		if (this.params.showInitialPrice)
		{
			this.price = Math.max(Number(this.params.startPrice) || 0, 0);
			this.animatedPrice = this.price;
		}

		this.initYadelivery();
	},

	beforeUnmount()
	{
		this.cleanupApi();

		if (this.animationFrameId)
		{
			cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = null;
		}
	},

	methods: {
		loc(messageId)
		{
			return BX.Loc.getMessage(messageId) || '';
		},

		cleanupApi()
		{
			if (this.api)
			{
				this.api.off('afterCalculate', this.onAfterCalculate);
				this.api.off('afterApplyPrice', this.onAfterApplyPrice);
				this.api.off('beforeCalculate', this.onBeforeCalculate);
				this.api = null;
			}
		},

		async initYadelivery()
		{
			const yaDelivery = window[MODULE_ID]?.run;
			if (!yaDelivery)
			{
				console.error('YaDelivery module is not loaded.');
				this.isInitializing = false;
				return;
			}

			this.cleanupApi();

			try
			{
				const componentSettings = this.getComponentSettings();
				const suggestions = this.params.enableSuggestions
					? await this.getSuggestionsOptions(this.params.typePrompts)
					: null;

				const api = await yaDelivery({
					customSettings: {
						isCustomCalculate: true,
						...componentSettings,
					},
					options: { suggestions },
				});

				this.api = api;
				api.on('afterCalculate', this.onAfterCalculate);
				api.on('afterApplyPrice', this.onAfterApplyPrice);
				api.on('beforeCalculate', this.onBeforeCalculate);
				this.setTotalResult();
			}
			catch (error)
			{
				console.error(error);
			}
			finally
			{
				this.isInitializing = false;
			}
		},

		showModal()
		{
			if (this.api)
			{
				this.api.showModal();
			}
			else if (typeof window.showYaDeliveryModal === 'function')
			{
				window.showYaDeliveryModal();
			}
		},

		toggleMapExpand()
		{
			this.mapExpanded = !this.mapExpanded;

			const mapEl = this.$refs.mapContainer;
			if (mapEl)
			{
				const onEnd = () => {
					mapEl.removeEventListener('transitionend', onEnd);
					this.api?.fitToViewport();
				};
				mapEl.addEventListener('transitionend', onEnd);
			}
			else
			{
				this.api?.fitToViewport();
			}
		},

		async onWarehouseChange(warehouseId)
		{
			if (warehouseId === this.selectedWarehouse)
			{
				return;
			}

			const savedAddress = this.address;
			this.selectedWarehouse = warehouseId;
			this.isChangingWarehouse = true;
			this.resetCalculation();

			const mapContainer = document.getElementById(MAP_CONTAINER_ID);
			if (mapContainer)
			{
				const alert = mapContainer.querySelector('.yadelivery-map__map-alert');
				mapContainer.innerHTML = '';
				if (alert)
				{
					mapContainer.appendChild(alert);
				}
			}

			await this.initYadelivery();
			this.isChangingWarehouse = false;

			if (savedAddress && this.api)
			{
				this.$nextTick(() => {
					this.api.calculateRoute(savedAddress);
				});
			}
		},
	},

	template: appTemplate,
};

BX.ready(() => {
	const container = document.getElementById('yadelivery-map-app');
	if (!container)
	{
		return;
	}

	let params = {};
	try
	{
		params = JSON.parse(container.dataset.params || '{}');
	}
	catch (e)
	{
		console.error('YaDeliveryMap: failed to parse params', e);
		return;
	}

	const app = BX.Vue3.BitrixVue.createApp(YaDeliveryMapApp, { params });
	app.mount(container);
});
